import { Component, effect, inject, signal, ElementRef, HostListener } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { GridModule } from '@progress/kendo-angular-grid';
import { IconsModule } from '@progress/kendo-angular-icons';
import { LoaderModule, IndicatorsModule } from '@progress/kendo-angular-indicators';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { BaseComponent } from '../../../core/shared/common/base.component';
import { TagsService } from '../../../core/services/tags.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { OrgDetections, Tag, DetectionSetting, AlertSyncControls } from '../../../core/models/entities';
import { DetectionSettingsService } from '../../../core/services/detection-settings-service';
import { PopupModule } from '@progress/kendo-angular-popup';
import { CommonModule, DatePipe } from '@angular/common';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogCloseResult, DialogModule, DialogRef, DialogService } from '@progress/kendo-angular-dialog';

@Component({
  selector: 'app-alert-preferences',
  standalone: true,
  imports: [
    LoaderModule,
    LayoutModule,
    ChartsModule,
    GridModule,
    FormsModule,
    ReactiveFormsModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    IconsModule,
    DropDownsModule,
    NavigationModule,
    IndicatorsModule,
    PopupModule,
    DialogModule,
    DatePipe,
    CommonModule,
  ],
  templateUrl: './alert-preferences.page.html',
  styleUrl: './alert-preferences.page.scss',
  providers: [TagsService],
})
export class AlertPreferencesPage extends BaseComponent {
  private route = inject(ActivatedRoute);
  private tagsService = inject(TagsService);
  detections = signal<OrgDetections[]>([]);
  filteredDetections = signal<OrgDetections[]>([]);
  private detectionsService = inject(DetectionSettingsService);
  error?: string;
  public isLoading = signal<boolean>(true);
  public availableTags = signal<Tag[]>([]);
  public selectedTags: Tag[] = [];
  organizationId = signal<number>(0);
  // Which alert-preference sync controls to show. Computed by the backend
  // (single source of truth). The "Propagate to child organizations" action maps
  // to propagate_to_children.
  public alertSyncControls = signal<AlertSyncControls>({
    propagate_to_children: false,
    stop_parent_sync: false,
  });
  // Current values of the org's sync settings, shown on the page when the
  // corresponding control is available (see alertSyncControls).
  public propagateEnabled = signal<boolean>(false);
  public stopParentSyncEnabled = signal<boolean>(false);
  public totalRecords = 0;
  private page = 0;
  public showNewOnly = false;
  public showTagFilter = false;
  private elementRef = inject(ElementRef);
  public updatingDetectionId = signal<number | null>(null);
  public isPropagating = signal<boolean>(false);
  private notificationService = inject(NotificationService);
  private dialogService = inject(DialogService);

  constructor() {
    super();
    this.initializeOrganization();
    this.initializeState();
  }

  private initializeOrganization(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const newOrgId = parseInt(params['organizationId']);
      if (newOrgId && newOrgId !== this.organizationId()) {
        this.isLoading.set(true);
        this.organizationId.set(newOrgId);
        this.detections.set([]);
        this.filteredDetections.set([]);
        this.page = 0;

        this.detectionsService.state = {
          skip: 0,
          take: 50,
          sort: [{ field: 'detection_id__title', dir: 'asc' }],
          filter: {
            logic: 'and',
            filters: [],
          },
        };

        this.loadTags();
        this.loadData();
        this.loadOrganizationMeta(newOrgId);
      }
    });
  }

  private loadOrganizationMeta(orgId: number): void {
    const defaults: AlertSyncControls = { propagate_to_children: false, stop_parent_sync: false };
    this.alertSyncControls.set(defaults);
    this.propagateEnabled.set(false);
    this.stopParentSyncEnabled.set(false);
    this.detectionsService
      .getOrganizationById(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (org) => {
          this.alertSyncControls.set(org?.alert_sync_controls ?? defaults);
          this.propagateEnabled.set(org?.propagate_alert_preferences_to_children ?? false);
          this.stopParentSyncEnabled.set(org?.stop_parent_synchronization ?? false);
        },
        error: () => {
          this.alertSyncControls.set(defaults);
          this.propagateEnabled.set(false);
          this.stopParentSyncEnabled.set(false);
        },
      });
  }

  private initializeState(): void {
    this.detectionsService.state = {
      skip: 0,
      take: 50,
      sort: [{ field: 'detection_id__title', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };

    this.loadTags();
    this.loadData();
  }

  loadData() {
    this.isLoading.set(true);
    this.detectionsService.getDetections(this.organizationId(), (data: any) => {
      if (data) {
        this.totalRecords = data.total;

        const pagingDetections: OrgDetections[] = data.data
          .map((d: any) => {
            const mapped = {
              detectionId: d.detection_id.pk,
              title: d.detection_id.title,
              category: d.detection_id.category.name,
              entityId: d.detection_id.entity_id,
              description: d.detection_id.description,
              status: d.detection_id.status,
              defaultState: d.detection_id.default_state,
              active: d.active,
              organizationId: d.organization_id,
              tags: d.detection_id.tags,
              createdAt: d.detection_id.created_at,
              updatedAt: d.detection_id.updated_at,
              isNew: d.is_new,
            };
            return mapped;
          })
          .sort((a: any, b: any) => a.title.localeCompare(b.title));

        if (this.page === 0) {
          this.detections.set(pagingDetections);
        } else {
          this.detections.update((current) => [...current, ...pagingDetections]);
        }

        this.filteredDetections.set(this.detections());
      }
      this.isLoading.set(false);
    });
  }

  onScroll(event: any) {
    const alertListContainer = event.target;
    const scrollTop = alertListContainer.scrollTop;
    const scrollHeight = alertListContainer.scrollHeight;
    const offsetHeight = alertListContainer.offsetHeight;

    const scrollThreshold = scrollHeight - offsetHeight - 10;

    if (scrollTop >= scrollThreshold && !this.isLoading() && this.detections().length < this.totalRecords) {
      this.page++;
      this.detectionsService.state = {
        ...this.detectionsService.state,
        skip: this.page * 50,
      };

      this.loadData();
    }
  }

  private loadTags(): void {
    this.tagsService.getTags().subscribe({
      next: (tags) => {
        this.availableTags.set(tags);
      },
      error: (error) => {
        console.error('Error loading tags:', error);
      },
    });
  }

  public onTagFilterChange(searchTerm: string): void {
    this.tagsService.searchTags(searchTerm).subscribe({
      next: (data) => {
        this.availableTags.set(data);
      },
      error: (error) => {
        console.error('Error adding new tag:', error);
      },
    });
  }

  public filterDetetections(): void {
    const newFilters = [];

    if (this.showNewOnly) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      newFilters.push({
        field: 'created_at',
        operator: 'gt',
        value: thirtyDaysAgo.toISOString(),
      });
    }

    if (this.selectedTags.length > 0) {
      const tagIds = this.selectedTags.map((tag) => tag.id).join(' or t/id eq ');
      newFilters.push({
        field: `detection_id/tags/any(t: t/id eq ${tagIds})`,
        operator: 'eq',
        value: true,
      });
    }

    this.detectionsService.state = {
      ...this.detectionsService.state,
      filter: {
        logic: 'and',
        filters: newFilters,
      },
    };

    this.page = 0;
    this.detections.set([]);
    this.filteredDetections.set([]);

    this.detectionsService.state.skip = 0;

    this.loadData();
  }

  public onTagRemove(event: any): void {
    this.selectedTags = this.selectedTags.filter((t) => t.id !== event.id);
    this.filterDetetections();
  }

  public onTagClear(): void {
    this.selectedTags = [];
    this.filterDetetections();
  }

  public onToggleChange(detection: OrgDetections, active: boolean): void {
    if (detection.detectionId !== null) {
      const updatedDetection: DetectionSetting = {
        detection_id: detection.detectionId,
        active: active,
      };

      this.updatingDetectionId.set(detection.detectionId);

      this.detections.update((detections) =>
        detections.map((d) => (d.detectionId === detection.detectionId ? { ...d, active: active } : d))
      );

      this.detectionsService.setOrganizationSettings(detection.organizationId!, updatedDetection).subscribe({
        next: () => {
          this.updatingDetectionId.set(null);
        },
        error: (error) => {
          console.error('Update failed', error);
          this.updatingDetectionId.set(null);
        },
      });
    } else {
      console.error('Invalid detection ID');
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    const popupElement = this.elementRef.nativeElement.querySelector('.tag-filter-popup');
    const buttonElement = this.elementRef.nativeElement.querySelector('.tag-filter-button');

    if (popupElement && buttonElement) {
      const clickedInside = popupElement.contains(targetElement) || buttonElement.contains(targetElement);
      if (!clickedInside) {
        this.showTagFilter = false;
      }
    }
  }

  public showNewOnlyChange(checked: boolean): void {
    this.showNewOnly = checked;
    this.filterDetetections();
  }

  public onPropagateClick(): void {
    const orgId = this.organizationId();
    if (!orgId || this.isPropagating()) return;

    const dialog: DialogRef = this.dialogService.open({
      title: 'Propagate Alert Preferences',
      content:
        'This will apply the current alert preferences to all child organizations and enable copy mode so new child organizations inherit these preferences. Continue?',
      actions: [
        { text: 'Cancel', primary: false },
        { text: 'Propagate', primary: true, themeColor: 'primary' },
      ],
      width: 480,
      height: 220,
    });

    dialog.result.subscribe((result) => {
      if (result instanceof DialogCloseResult || (result as any).text !== 'Propagate') {
        return;
      }

      this.isPropagating.set(true);
      this.detectionsService
        .propagateAlertPreferences(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.isPropagating.set(false);
            this.showNotification('Alert preferences propagation enqueued', 'success');
          },
          error: (error) => {
            console.error('Propagation failed', error);
            this.isPropagating.set(false);
            this.showNotification('Failed to propagate alert preferences', 'error');
          },
        });
    });
  }

  private showNotification(message: string, type: 'info' | 'success' | 'warning' | 'error'): void {
    this.notificationService.show({
      content: message,
      cssClass: 'notification-' + type,
      animation: { type: 'slide', duration: 400 },
      hideAfter: 3000,
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true },
    });
  }
}
